import { clearActiveContext, getActiveContext } from '@/shared/activeContext';
import { translate } from '@/shared/i18n/translate';
import type { TranslationKey } from '@/shared/i18n/types';
import type { ErrorResponse } from './contracts/apiContracts';

const SAME_ORIGIN_API_HOSTS = new Set([
  'localhost',
  '127.0.0.1',
  'pay.ru.tuna.am',
  'paym.ru.tuna.am',
]);

/** Relative /api on dev and tuna tunnels (Vite/nginx proxy). Ignores VITE_API_BASE_URL there. */
function resolveApiBase(): string {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname.toLowerCase();
    if (SAME_ORIGIN_API_HOSTS.has(host)) {
      return '';
    }
  }

  return import.meta.env.VITE_API_BASE_URL?.trim() ?? '';
}

const API_BASE = resolveApiBase();

export interface ApiErrorBody {
  code: string;
  message?: string;
  details?: string;
  params?: Record<string, string>;
  traceId?: string;
  fieldErrors?: Record<string, string[]>;
}

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly details?: string,
    public readonly params?: Record<string, string>,
    public readonly traceId?: string,
    public readonly fieldErrors?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const ERROR_CODE_KEYS: Record<string, TranslationKey> = {
  invalid_credentials: 'api.error.invalid_credentials',
  empty_file: 'api.error.empty_file',
  pattern_required: 'api.error.pattern_required',
  syrve_counterparty_required: 'api.error.syrve_counterparty_required',
  syrve_counterparty_name_required: 'api.error.syrve_counterparty_name_required',
  bank_transaction_required: 'api.error.bank_transaction_required',
  bank_transaction_not_found: 'api.error.bank_transaction_not_found',
  unauthorized: 'api.error.unauthorized',
  'permission_denied_for_scope': 'api.error.permission_denied_for_scope',
  forbidden: 'api.error.forbidden',
  not_found: 'api.error.not_found',
  'validation.failed': 'api.error.validation_failed',
  'concurrency.conflict': 'api.error.concurrency_conflict',
  'workflow.invalid_transition': 'api.error.workflow_invalid_transition',
  'workflow.locked': 'api.error.workflow_locked',
  'allocation.not_editable_after_approve': 'api.error.allocation_not_editable_after_approve',
  'allocation.not_editable_after_queued': 'api.error.allocation_not_editable_after_queued',
  'allocation.already_in_flight': 'api.error.allocation_already_in_flight',
  internal_error: 'api.error.internal_error',
  'context.location.required': 'api.error.context_location_required',
  'context.organization.required': 'api.error.context_organization_required',
  'request.failed': 'api.error.request_failed',
  organization_default_not_deletable: 'api.error.organization_default_not_deletable',
  counterparty_rule_duplicate_active: 'api.error.counterparty_rule_duplicate_active',
  bank_reprocess_preview_forbidden: 'api.error.bank_reprocess_preview_forbidden',
  review_override_reason_required: 'api.error.review_override_reason_required',
  payment_write_disabled: 'api.error.payment_write_disabled',
  counterparty_match_not_manual: 'api.error.counterparty_match_not_manual',
  counterparty_match_allocation_blocked: 'api.error.counterparty_match_allocation_blocked',
  bank_import_has_allocations: 'api.error.bank_import_has_allocations',
  import_delete_reason_required: 'api.error.import_delete_reason_required',
  bank_account_resolution_required: 'workbench.v1.error.bank_account_resolution_required',
  financial_counterparty_not_selectable: 'workbench.v1.error.financial_counterparty_not_selectable',
  financial_counterparty_connection_scope_invalid: 'workbench.v1.error.financial_counterparty_connection_scope_invalid',
  financial_counterparty_resolution_row_version_required: 'workbench.v1.error.concurrency',
  counterparty_source_unavailable: 'workbench.v1.error.financial_counterparty_source_unavailable',
};

function localizeError(
  code: string,
  legacyMessage?: string,
  params?: Record<string, string>,
  traceId?: string,
): string {
  const key = ERROR_CODE_KEYS[code];
  if (key) {
    return translate(key, {
      ...params,
      traceId: traceId?.trim() || '—',
    });
  }
  return legacyMessage ?? translate('api.error.request_failed');
}

export function clearAuthTokens() {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  clearActiveContext();
}

function getAuthToken(): string | null {
  return localStorage.getItem('accessToken');
}

let loginRedirectScheduled = false;

function redirectToLoginOnUnauthorized() {
  clearAuthTokens();
  if (window.location.pathname === '/login' || loginRedirectScheduled) {
    return;
  }

  loginRedirectScheduled = true;
  window.location.assign('/login');
}

export async function httpClient<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  const token = getAuthToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  const activeContext = getActiveContext();
  if (activeContext?.organizationId) {
    headers.set('X-Organization-Id', activeContext.organizationId);
    if (activeContext.locationId) {
      headers.set('X-Location-Id', activeContext.locationId);
    } else {
      headers.delete('X-Location-Id');
    }
  }

  if (!(init.body instanceof FormData) && !headers.has('Content-Type') && init.body) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
  });

  if (!response.ok) {
    if (response.status === 401) {
      redirectToLoginOnUnauthorized();
    }

    let body: (ErrorResponse & ApiErrorBody) | null = null;
    try {
      body = (await response.json()) as ErrorResponse & ApiErrorBody;
    } catch {
      // Response body is not JSON; keep default error mapping below.
    }

    const code = body?.code ?? (
      response.status === 401
        ? 'unauthorized'
        : response.status === 403
          ? 'forbidden'
          : response.status === 404
            ? 'not_found'
            : response.status === 409
              ? 'concurrency.conflict'
              : 'request.failed'
    );
    throw new ApiError(
      code,
      localizeError(code, body?.message, body?.params, body?.traceId),
      body?.details,
      body?.params,
      body?.traceId,
      body?.fieldErrors,
    );
  }

  if (response.status === 204) {
    return null as T;
  }

  return (await response.json()) as T;
}

export async function downloadFile(path: string): Promise<{ blob: Blob; fileName: string }> {
  const headers = new Headers();
  const token = getAuthToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const activeContext = getActiveContext();
  if (activeContext?.organizationId) {
    headers.set('X-Organization-Id', activeContext.organizationId);
    if (activeContext.locationId) {
      headers.set('X-Location-Id', activeContext.locationId);
    }
  }

  const response = await fetch(`${API_BASE}${path}`, { headers });
  if (!response.ok) {
    if (response.status === 401) {
      redirectToLoginOnUnauthorized();
    }

    let body: (ErrorResponse & ApiErrorBody) | null = null;
    try {
      body = (await response.json()) as ErrorResponse & ApiErrorBody;
    } catch {
      // Binary endpoint can still return an empty or non-JSON proxy error.
    }
    const code = body?.code ?? (response.status === 403 ? 'forbidden' : 'request.failed');
    throw new ApiError(code, localizeError(code, body?.message, body?.params, body?.traceId), body?.details, body?.params, body?.traceId);
  }

  const disposition = response.headers.get('content-disposition') ?? '';
  const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(disposition);
  const fileName = decodeURIComponent(match?.[1] ?? match?.[2] ?? 'download');
  const contentType = response.headers.get('content-type')?.split(';', 1)[0]?.trim() ?? '';
  const buffer = await response.arrayBuffer();
  const blob = contentType
    ? new Blob([buffer], { type: contentType })
    : new Blob([buffer]);
  return { blob, fileName };
}
